import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCart, X, Plus, Minus, ArrowRight, Loader2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCart } from '@/contexts/CartContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Store = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [processingOrder, setProcessingOrder] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  const { cartItems, addToCart, removeFromCart, updateQuantity, clearCart, getCartTotal, getCartCount } = useCart();

  const [checkoutForm, setCheckoutForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postal_code: ''
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product) => {
    addToCart(product);
    toast.success('Added to cart!');
  };

  const handleCheckout = async (e) => {
    e.preventDefault();
    setProcessingOrder(true);

    try {
      const orderData = {
        ...checkoutForm,
        items: cartItems.map(item => ({
          product_id: item.product.id,
          quantity: item.quantity
        })),
        total: getCartTotal()
      };

      const response = await axios.post(`${API}/checkout`, orderData);
      toast.success(response.data.message);
      clearCart();
      setCheckoutOpen(false);
      setCartOpen(false);
      setCheckoutForm({
        name: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        postal_code: ''
      });
    } catch (error) {
      toast.error('Failed to process order. Please try again.');
    } finally {
      setProcessingOrder(false);
    }
  };

  const filteredProducts = selectedCategory === 'all' 
    ? products
    : products.filter(p => p.category === selectedCategory);

  const fadeInUp = {
    initial: { opacity: 0, y: 40 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <div className="w-full" data-testid="store-page">
      {/* Hero */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://images.pexels.com/photos/1884574/pexels-photo-1884574.jpeg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>
        </div>

        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
          <motion.div {...fadeInUp}>
            <h1 className="hero-title text-5xl sm:text-6xl lg:text-7xl text-white mb-6" data-testid="page-title">
              Travel Merch
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              Travel essentials and gifts for your next adventure
            </p>
          </motion.div>
        </div>

        {/* Floating Cart Button */}
        <button
          onClick={() => setCartOpen(true)}
          className="fixed top-24 right-6 z-40 bg-[#1E6FBA] text-white rounded-full p-4 shadow-lg hover:bg-[#1558A0] transition-colors btn-scale"
          data-testid="cart-button"
        >
          <ShoppingCart className="w-6 h-6" />
          {getCartCount() > 0 && (
            <span className="absolute -top-2 -right-2 bg-[#F28A30] text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
              {getCartCount()}
            </span>
          )}
        </button>
      </section>

      {/* Products */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="products-section">
        {/* Category Filter */}
        <div className="flex justify-center gap-4 mb-12">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-6 py-2 rounded-full font-medium transition-colors ${
              selectedCategory === 'all'
                ? 'bg-[#1E6FBA] text-white'
                : 'bg-gray-200 text-[#4A4A4A] hover:bg-gray-300'
            }`}
            data-testid="filter-all"
          >
            All
          </button>
          <button
            onClick={() => setSelectedCategory('travel')}
            className={`px-6 py-2 rounded-full font-medium transition-colors ${
              selectedCategory === 'travel'
                ? 'bg-[#1E6FBA] text-white'
                : 'bg-gray-200 text-[#4A4A4A] hover:bg-gray-300'
            }`}
            data-testid="filter-travel"
          >
            Travel
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#1E6FBA]" />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProducts.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-2xl overflow-hidden shadow-sm card-hover"
                data-testid={`product-${product.id}`}
              >
                <div className="relative h-64">
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {product.name}
                  </h3>
                  <p className="text-[#4A4A4A] text-sm mb-4 leading-relaxed">
                    {product.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-[#2E8B57]">
                      ${product.price.toFixed(2)}
                    </span>
                    <Button
                      onClick={() => handleAddToCart(product)}
                      className="btn-scale bg-[#1E6FBA] hover:bg-[#1558A0] text-white"
                      data-testid={`add-to-cart-${product.id}`}
                    >
                      Add to Cart
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {/* Cart Sidebar */}
      <AnimatePresence>
        {cartOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50"
              onClick={() => setCartOpen(false)}
              data-testid="cart-overlay"
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween' }}
              className="fixed right-0 top-0 bottom-0 w-full sm:w-96 bg-white z-50 shadow-2xl overflow-y-auto"
              data-testid="cart-sidebar"
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Your Cart
                  </h2>
                  <button
                    onClick={() => setCartOpen(false)}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    data-testid="close-cart"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                {cartItems.length === 0 ? (
                  <div className="text-center py-12">
                    <ShoppingCart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-[#757575]">Your cart is empty</p>
                  </div>
                ) : (
                  <>
                    <div className="space-y-4 mb-6">
                      {cartItems.map((item) => (
                        <div key={item.product.id} className="flex gap-4 border-b pb-4" data-testid={`cart-item-${item.product.id}`}>
                          <img
                            src={item.product.image_url}
                            alt={item.product.name}
                            className="w-20 h-20 object-cover rounded-lg"
                          />
                          <div className="flex-1">
                            <h3 className="font-semibold mb-1">{item.product.name}</h3>
                            <p className="text-[#2E8B57] font-bold mb-2">
                              ${item.product.price.toFixed(2)}
                            </p>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                                className="p-1 hover:bg-gray-100 rounded transition-colors"
                                data-testid={`decrease-quantity-${item.product.id}`}
                              >
                                <Minus className="w-4 h-4" />
                              </button>
                              <span className="w-8 text-center">{item.quantity}</span>
                              <button
                                onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                                className="p-1 hover:bg-gray-100 rounded transition-colors"
                                data-testid={`increase-quantity-${item.product.id}`}
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              removeFromCart(item.product.id);
                              toast.success('Removed from cart');
                            }}
                            className="text-red-500 hover:text-red-700 transition-colors"
                            data-testid={`remove-item-${item.product.id}`}
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                      ))}
                    </div>

                    <div className="border-t pt-4 mb-6">
                      <div className="flex justify-between text-xl font-bold mb-4">
                        <span>Total:</span>
                        <span className="text-[#2E8B57]" data-testid="cart-total">${getCartTotal().toFixed(2)}</span>
                      </div>
                      <Button
                        onClick={() => {
                          setCartOpen(false);
                          setCheckoutOpen(true);
                        }}
                        className="w-full btn-scale bg-[#1E6FBA] hover:bg-[#1558A0] text-white py-6 text-lg"
                        data-testid="proceed-to-checkout"
                      >
                        Proceed to Checkout
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Checkout Modal */}
      <AnimatePresence>
        {checkoutOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
              onClick={() => setCheckoutOpen(false)}
              data-testid="checkout-overlay"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-white rounded-3xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-testid="checkout-modal">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-3xl" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Checkout
                  </h2>
                  <button
                    onClick={() => setCheckoutOpen(false)}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    data-testid="close-checkout"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <form onSubmit={handleCheckout} className="space-y-6">
                  <div>
                    <Label htmlFor="checkout-name">Full Name</Label>
                    <Input
                      id="checkout-name"
                      type="text"
                      required
                      value={checkoutForm.name}
                      onChange={(e) => setCheckoutForm({ ...checkoutForm, name: e.target.value })}
                      data-testid="checkout-name-input"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="checkout-email">Email</Label>
                      <Input
                        id="checkout-email"
                        type="email"
                        required
                        value={checkoutForm.email}
                        onChange={(e) => setCheckoutForm({ ...checkoutForm, email: e.target.value })}
                        data-testid="checkout-email-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="checkout-phone">Phone</Label>
                      <Input
                        id="checkout-phone"
                        type="tel"
                        required
                        value={checkoutForm.phone}
                        onChange={(e) => setCheckoutForm({ ...checkoutForm, phone: e.target.value })}
                        data-testid="checkout-phone-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="checkout-address">Street Address</Label>
                    <Input
                      id="checkout-address"
                      type="text"
                      required
                      value={checkoutForm.address}
                      onChange={(e) => setCheckoutForm({ ...checkoutForm, address: e.target.value })}
                      data-testid="checkout-address-input"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="checkout-city">City</Label>
                      <Input
                        id="checkout-city"
                        type="text"
                        required
                        value={checkoutForm.city}
                        onChange={(e) => setCheckoutForm({ ...checkoutForm, city: e.target.value })}
                        data-testid="checkout-city-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="checkout-postal">Postal Code</Label>
                      <Input
                        id="checkout-postal"
                        type="text"
                        required
                        value={checkoutForm.postal_code}
                        onChange={(e) => setCheckoutForm({ ...checkoutForm, postal_code: e.target.value })}
                        data-testid="checkout-postal-input"
                      />
                    </div>
                  </div>

                  <div className="border-t pt-6">
                    <div className="flex justify-between text-2xl font-bold mb-6">
                      <span>Total:</span>
                      <span className="text-[#2E8B57]">${getCartTotal().toFixed(2)}</span>
                    </div>
                    <Button
                      type="submit"
                      className="w-full btn-scale bg-[#2E8B57] hover:bg-[#26734B] text-white py-6 text-lg"
                      disabled={processingOrder}
                      data-testid="place-order-button"
                    >
                      {processingOrder ? (
                        <>
                          <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <Check className="mr-2 w-5 h-5" />
                          Place Order
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Store;
